/* eslint-disable no-unused-vars */
import { useState, useContext, useEffect, useRef } from "react";
import { AdminContext } from "../../../context/AdminContext";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Filter,
  Plus,
  Eye,
  Edit3,
  Trash2,
  Star,
  Pin,
  Send,
  MoreVertical,
  CheckSquare,
  FileText,
  Clock,
  Globe,
  Lock,
  Crown,
  ArrowUpDown,
  ChevronDown,
  RefreshCw,
} from "lucide-react";
import { useTheme } from "../../../context/ThemeContext";
import Pagination from "../../../components/Pagination";

const statusColors = {
  draft: "bg-yellow-600 text-white dark:bg-yellow-700 dark:text-white",
  published: "bg-green-600 text-white dark:bg-green-700 dark:text-white",
  scheduled: "bg-blue-600 text-white dark:bg-blue-700 dark:text-white",
  archived: "bg-gray-600 text-white dark:bg-gray-700 dark:text-white",
};

const visibilityIcons = {
  public: Globe,
  private: Lock,
  premium: Crown,
};

const BlogList = () => {
  const {
    blogs,
    blogPagination,
    blogCategories,
    fetchBlogs,
    fetchBlogCategories,
    deleteBlog,
    publishBlog,
    unpublishBlog,
    toggleFeatured,
    togglePinned,
    bulkPublishBlogs,
    bulkDeleteBlogs,
  } = useContext(AdminContext);
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState(""); // Separate input state
  const [filterStatus, setFilterStatus] = useState("All");
  const [filterCategory, setFilterCategory] = useState("");
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [selectedBlogs, setSelectedBlogs] = useState([]);
  const [activeMenu, setActiveMenu] = useState(null);
  const [loading, setLoading] = useState(true);
  const statusDropdownRef = useRef(null);
  const categoryDropdownRef = useRef(null);

  useEffect(() => {
    fetchBlogCategories();
  }, [fetchBlogCategories]);

  useEffect(() => {
    loadBlogs();
  }, [blogPagination.page, filterStatus, filterCategory]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (event.target.closest(".action-menu")) return;
      setActiveMenu(null);

      if (
        statusDropdownRef.current &&
        !statusDropdownRef.current.contains(event.target)
      ) {
        setShowStatusDropdown(false);
      }

      if (
        categoryDropdownRef.current &&
        !categoryDropdownRef.current.contains(event.target)
      ) {
        setShowCategoryDropdown(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const loadBlogs = async () => {
    setLoading(true);
    await fetchBlogs({
      page: blogPagination.page,
      limit: blogPagination.limit,
      status: filterStatus !== "All" ? filterStatus : undefined,
      category: filterCategory || undefined,
      search: searchTerm || undefined,
    });
    setLoading(false);
  };

  const handleSearch = () => {
    setSearchTerm(searchInput);
    loadBlogs();
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleResetFilters = () => {
    setSearchInput("");
    setSearchTerm("");
    setFilterStatus("All");
    setFilterCategory("");
    loadBlogs();
  };

  const handlePageChange = (page) => {
    fetchBlogs({
      page,
      limit: blogPagination.limit,
      status: filterStatus !== "All" ? filterStatus : undefined,
      category: filterCategory || undefined,
      search: searchTerm || undefined,
    });
  };

  const handleItemsPerPageChange = (limit) => {
    fetchBlogs({
      page: 1,
      limit,
      status: filterStatus !== "All" ? filterStatus : undefined,
      category: filterCategory || undefined,
      search: searchTerm || undefined,
    });
  };

  const toggleSelect = (blogId) => {
    setSelectedBlogs((prev) =>
      prev.includes(blogId)
        ? prev.filter((id) => id !== blogId)
        : [...prev, blogId],
    );
  };

  const toggleSelectAll = () => {
    if (selectedBlogs.length === blogs.length) {
      setSelectedBlogs([]);
    } else {
      setSelectedBlogs(blogs.map((b) => b.id));
    }
  };

  const handleBulkPublish = async () => {
    if (selectedBlogs.length === 0) return;
    await bulkPublishBlogs(selectedBlogs);
    setSelectedBlogs([]);
    loadBlogs();
  };

  const handleBulkDelete = async () => {
    if (selectedBlogs.length === 0) return;
    if (!confirm(`Delete ${selectedBlogs.length} blog(s)?`)) return;
    await bulkDeleteBlogs(selectedBlogs);
    setSelectedBlogs([]);
    loadBlogs();
  };

  const handleDelete = async (blogId) => {
    if (!confirm("Delete this blog?")) return;
    await deleteBlog(blogId);
  };

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 pb-12">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-text dark:text-dark-text">
            Parent Hub — Blog Posts
          </h1>
          <p className="text-gray-500 dark:text-dark-muted mt-1 text-sm sm:text-base">
            Manage, publish, and organize blog content for parents.
            {blogPagination.total > 0 && (
              <span className="ml-2 font-semibold text-primary">
                ({blogPagination.total} total)
              </span>
            )}
          </p>
        </div>
        <button
          onClick={() => navigate("/parent-hub/blogs/new")}
          className="flex items-center justify-center gap-2 px-5 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-xl text-sm font-bold transition-colors shadow-lg shadow-primary/20 dark:shadow-none"
        >
          <Plus size={18} /> New Blog Post
        </button>
      </div>

      {/* Search & Filters */}
      <div className="bg-card dark:bg-dark-surface p-1.5 rounded-2xl shadow-sm border border-gray-100 dark:border-dark-border mb-6 flex gap-2">
        {/* Search Input */}
        <div className="relative flex-1">
          <Search
            className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
            size={20}
          />
          <input
            type="text"
            placeholder="Search blogs..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyPress={handleSearchKeyPress}
            className="w-full pl-12 pr-4 py-3 rounded-xl bg-accent dark:bg-gray-900 border border-transparent focus:bg-card dark:focus:bg-dark-surface focus:border-primary outline-none text-sm transition-all text-text dark:text-dark-text placeholder-gray-400"
          />
        </div>

        {/* Search Button */}
        <button
          onClick={handleSearch}
          className="flex items-center justify-center px-4 py-3 bg-primary hover:bg-primary/90 text-white rounded-xl text-sm font-medium transition-colors"
        >
          <Search size={16} />
        </button>

        {/* Status Filter Dropdown */}
        <div className="relative" ref={statusDropdownRef}>
          <button
            onClick={() => setShowStatusDropdown(!showStatusDropdown)}
            className="flex items-center gap-2 px-4 py-3 bg-accent dark:bg-gray-900 text-gray-600 dark:text-dark-muted rounded-xl text-sm font-medium border border-transparent hover:border-gray-200 dark:hover:border-dark-border transition-colors whitespace-nowrap"
          >
            <Filter size={16} />
            {filterStatus === "All"
              ? "All Statuses"
              : filterStatus.charAt(0).toUpperCase() + filterStatus.slice(1)}
            <ChevronDown
              size={14}
              className={`transition-transform ${
                showStatusDropdown ? "rotate-180" : ""
              }`}
            />
          </button>

          {showStatusDropdown && (
            <div className="absolute right-0 mt-2 w-48 bg-card dark:bg-dark-surface rounded-xl shadow-xl border border-gray-100 dark:border-dark-border p-1 z-50 animate-in fade-in zoom-in-95 duration-200">
              {["All", "draft", "published", "scheduled", "archived"].map(
                (status) => (
                  <button
                    key={status}
                    onClick={() => {
                      setFilterStatus(status === "All" ? "All" : status);
                      setShowStatusDropdown(false);
                    }}
                    className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      filterStatus === status ||
                      (filterStatus === "All" && status === "All")
                        ? "bg-primary text-white dark:bg-primary"
                        : "text-gray-600 dark:text-dark-muted hover:bg-accent dark:hover:bg-gray-800"
                    }`}
                  >
                    {status === "All"
                      ? "All Statuses"
                      : status.charAt(0).toUpperCase() + status.slice(1)}
                  </button>
                ),
              )}
            </div>
          )}
        </div>

        {/* Category Filter Dropdown */}
        <div className="relative" ref={categoryDropdownRef}>
          <button
            onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
            className="flex items-center gap-2 px-4 py-3 bg-accent dark:bg-gray-900 text-gray-600 dark:text-dark-muted rounded-xl text-sm font-medium border border-transparent hover:border-gray-200 dark:hover:border-dark-border transition-colors whitespace-nowrap"
          >
            <FileText size={16} />
            {filterCategory
              ? blogCategories.find((c) => c.id === filterCategory)?.nameEn ||
                "Category"
              : "All Categories"}
            <ChevronDown
              size={14}
              className={`transition-transform ${
                showCategoryDropdown ? "rotate-180" : ""
              }`}
            />
          </button>

          {showCategoryDropdown && (
            <div className="absolute right-0 mt-2 w-56 bg-card dark:bg-dark-surface rounded-xl shadow-xl border border-gray-100 dark:border-dark-border p-1 z-50 animate-in fade-in zoom-in-95 duration-200 max-h-80 overflow-y-auto">
              <button
                onClick={() => {
                  setFilterCategory("");
                  setShowCategoryDropdown(false);
                }}
                className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  !filterCategory
                    ? "bg-primary text-white dark:bg-primary"
                    : "text-gray-600 dark:text-dark-muted hover:bg-accent dark:hover:bg-gray-800"
                }`}
              >
                All Categories
              </button>
              {blogCategories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => {
                    setFilterCategory(cat.id);
                    setShowCategoryDropdown(false);
                  }}
                  className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    filterCategory === cat.id
                      ? "bg-primary text-white dark:bg-primary"
                      : "text-gray-600 dark:text-dark-muted hover:bg-accent dark:hover:bg-gray-800"
                  }`}
                >
                  {cat.icon} {cat.nameEn || cat.name}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Reset Button */}
        <button
          onClick={handleResetFilters}
          className="flex items-center justify-center px-4 py-3 rounded-xl bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 font-semibold hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors border border-gray-200 dark:border-gray-700"
          title="Reset Filters"
        >
          <RefreshCw size={18} />
        </button>
      </div>

      {/* Bulk Actions */}
      {selectedBlogs.length > 0 && (
        <div className="bg-primary/10 dark:bg-primary/20 border border-primary/30 rounded-2xl p-4 mb-6 flex items-center justify-between">
          <span className="text-sm font-semibold text-primary">
            {selectedBlogs.length} blog(s) selected
          </span>
          <div className="flex gap-3">
            <button
              onClick={handleBulkPublish}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-bold rounded-xl transition-colors"
            >
              <Send size={14} className="inline mr-1" /> Publish All
            </button>
            <button
              onClick={handleBulkDelete}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-bold rounded-xl transition-colors"
            >
              <Trash2 size={14} className="inline mr-1" /> Delete All
            </button>
          </div>
        </div>
      )}

      {/* Blog Table */}
      <div className="bg-card dark:bg-dark-surface rounded-3xl shadow-sm border border-gray-100 dark:border-dark-border overflow-visible">
        {loading ? (
          <div className="text-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="text-gray-500 dark:text-dark-muted mt-4 font-medium">
              Loading blogs...
            </p>
          </div>
        ) : blogs.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-gray-400 dark:text-dark-muted" />
            </div>
            <h3 className="text-lg font-semibold text-text dark:text-dark-text mb-2">
              No blog posts found
            </h3>
            <p className="text-gray-500 dark:text-dark-muted mb-6">
              {searchTerm || filterStatus !== "All" || filterCategory
                ? "Try adjusting your search or filter criteria."
                : "Get started by creating your first blog post."}
            </p>
            {!searchTerm && filterStatus === "All" && !filterCategory && (
              <button
                onClick={() => navigate("/parent-hub/blogs/new")}
                className="px-6 py-3 bg-primary hover:bg-primary/90 text-white rounded-xl font-bold transition-colors shadow-lg shadow-primary/20"
              >
                Create Your First Blog
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-visible min-h-[400px]">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 dark:border-dark-border">
                  <th className="text-left p-4 w-10">
                    <input
                      type="checkbox"
                      checked={
                        selectedBlogs.length === blogs.length &&
                        blogs.length > 0
                      }
                      onChange={toggleSelectAll}
                      className="rounded border-gray-300 dark:border-gray-600 text-primary focus:ring-primary"
                    />
                  </th>
                  <th className="text-left p-4 font-semibold text-gray-600 dark:text-dark-muted">
                    Blog
                  </th>
                  <th className="text-left p-4 font-semibold text-gray-600 dark:text-dark-muted hidden md:table-cell">
                    Category
                  </th>
                  <th className="text-left p-4 font-semibold text-gray-600 dark:text-dark-muted">
                    Status
                  </th>
                  <th className="text-left p-4 font-semibold text-gray-600 dark:text-dark-muted hidden lg:table-cell">
                    <div className="flex items-center gap-1">
                      Views <ArrowUpDown size={12} />
                    </div>
                  </th>
                  <th className="text-left p-4 font-semibold text-gray-600 dark:text-dark-muted hidden xl:table-cell">
                    Date
                  </th>
                  <th className="text-right p-4 font-semibold text-gray-600 dark:text-dark-muted">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {blogs.map((blog) => {
                  const VisIcon = visibilityIcons[blog.visibility] || Globe;
                  return (
                    <tr
                      key={blog.id}
                      className="border-b border-gray-50 dark:border-dark-border/50 hover:bg-gray-50/50 dark:hover:bg-gray-800/30 transition-colors"
                    >
                      <td className="p-4">
                        <input
                          type="checkbox"
                          checked={selectedBlogs.includes(blog.id)}
                          onChange={() => toggleSelect(blog.id)}
                          className="rounded border-gray-300 dark:border-gray-600 text-primary focus:ring-primary"
                        />
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          {blog.coverImage ? (
                            <img
                              src={blog.coverImage}
                              alt=""
                              className="w-12 h-12 rounded-xl object-cover flex-shrink-0"
                            />
                          ) : (
                            <div className="w-12 h-12 rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0">
                              <FileText size={18} className="text-gray-400" />
                            </div>
                          )}
                          <div className="min-w-0">
                            <p className="font-semibold text-text dark:text-dark-text truncate max-w-[200px] lg:max-w-[300px]">
                              {blog.titleEn || "Untitled"}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <VisIcon size={12} className="text-gray-400" />
                              <span className="text-xs text-gray-400">
                                {blog.readingTime} min read
                              </span>
                              {blog.isFeatured && (
                                <Star
                                  size={12}
                                  className="text-yellow-500 fill-yellow-500"
                                />
                              )}
                              {blog.isPinned && (
                                <Pin
                                  size={12}
                                  className="text-primary fill-primary"
                                />
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="p-4 hidden md:table-cell">
                        {blog.category && (
                          <span
                            className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold border"
                            style={{
                              backgroundColor: `${blog.category.color}15`,
                              color: blog.category.color,
                              borderColor: `${blog.category.color}30`,
                            }}
                          >
                            {blog.category.icon}{" "}
                            {blog.category.nameEn || blog.category.name}
                          </span>
                        )}
                      </td>
                      <td className="p-4">
                        <span
                          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${statusColors[blog.status]}`}
                        >
                          {blog.status.charAt(0).toUpperCase() +
                            blog.status.slice(1)}
                        </span>
                      </td>
                      <td className="p-4 hidden lg:table-cell">
                        <span className="font-semibold text-text dark:text-dark-text">
                          {blog.viewCount?.toLocaleString() || 0}
                        </span>
                      </td>
                      <td className="p-4 hidden xl:table-cell text-gray-500 dark:text-dark-muted text-xs">
                        {new Date(
                          blog.publishedAt || blog.createdAt,
                        ).toLocaleDateString("en-GB", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })}
                      </td>
                      <td className="p-4 text-right relative action-menu">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveMenu(
                              activeMenu === blog.id ? null : blog.id,
                            );
                          }}
                          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg text-gray-500 dark:text-dark-muted hover:text-text dark:hover:text-dark-text transition-colors"
                          title="Blog actions"
                        >
                          <MoreVertical size={18} />
                        </button>

                        {activeMenu === blog.id && (
                          <div className="absolute right-4 top-full mt-2 w-52 bg-white dark:bg-dark-surface rounded-lg shadow-lg border border-gray-200 dark:border-dark-border z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200 py-1">
                            <button
                              onClick={() => {
                                navigate(`/parent-hub/blogs/edit/${blog.id}`);
                                setActiveMenu(null);
                              }}
                              className="w-full text-left px-4 py-2.5 text-sm text-gray-700 dark:text-dark-text hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-2 transition-colors"
                            >
                              <Edit3
                                size={16}
                                className="text-gray-500 dark:text-gray-400"
                              />
                              <span>Edit</span>
                            </button>
                            <button
                              onClick={() => {
                                navigate(
                                  `/parent-hub/blogs/preview/${blog.id}`,
                                );
                                setActiveMenu(null);
                              }}
                              className="w-full text-left px-4 py-2.5 text-sm text-gray-700 dark:text-dark-text hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-2 transition-colors"
                            >
                              <Eye
                                size={16}
                                className="text-gray-500 dark:text-gray-400"
                              />
                              <span>Preview</span>
                            </button>
                            {blog.status !== "published" ? (
                              <button
                                onClick={() => {
                                  publishBlog(blog.id);
                                  setActiveMenu(null);
                                }}
                                className="w-full text-left px-4 py-2.5 text-sm text-gray-700 dark:text-dark-text hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-2 transition-colors"
                              >
                                <Send
                                  size={16}
                                  className="text-gray-500 dark:text-gray-400"
                                />
                                <span>Publish</span>
                              </button>
                            ) : (
                              <button
                                onClick={() => {
                                  unpublishBlog(blog.id);
                                  setActiveMenu(null);
                                }}
                                className="w-full text-left px-4 py-2.5 text-sm text-gray-700 dark:text-dark-text hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-2 transition-colors"
                              >
                                <Clock
                                  size={16}
                                  className="text-gray-500 dark:text-gray-400"
                                />
                                <span>Unpublish</span>
                              </button>
                            )}
                            <button
                              onClick={() => {
                                toggleFeatured(blog.id);
                                setActiveMenu(null);
                              }}
                              className="w-full text-left px-4 py-2.5 text-sm text-gray-700 dark:text-dark-text hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-2 transition-colors"
                            >
                              <Star
                                size={16}
                                className={
                                  blog.isFeatured
                                    ? "text-yellow-500 fill-current"
                                    : "text-gray-500 dark:text-gray-400"
                                }
                              />
                              <span>
                                {blog.isFeatured ? "Unfeature" : "Feature"}
                              </span>
                            </button>
                            <button
                              onClick={() => {
                                togglePinned(blog.id);
                                setActiveMenu(null);
                              }}
                              className="w-full text-left px-4 py-2.5 text-sm text-gray-700 dark:text-dark-text hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-2 transition-colors"
                            >
                              <Pin
                                size={16}
                                className={
                                  blog.isPinned
                                    ? "text-primary fill-current"
                                    : "text-gray-500 dark:text-gray-400"
                                }
                              />
                              <span>{blog.isPinned ? "Unpin" : "Pin"}</span>
                            </button>
                            <div className="h-px bg-gray-100 dark:bg-gray-700 my-1"></div>
                            <button
                              onClick={() => {
                                handleDelete(blog.id);
                                setActiveMenu(null);
                              }}
                              className="w-full text-left px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2 transition-colors"
                            >
                              <Trash2 size={16} />
                              <span>Delete</span>
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {blogPagination.totalPages > 0 && (
        <div className="border-t border-gray-100 dark:border-dark-border bg-gray-50/50 dark:bg-gray-800/30 p-2 mt-6">
          <Pagination
            currentPage={blogPagination.page}
            totalPages={blogPagination.totalPages}
            onPageChange={handlePageChange}
            itemsPerPage={blogPagination.limit}
            onItemsPerPageChange={handleItemsPerPageChange}
            totalItems={blogPagination.total}
            loading={loading}
          />
        </div>
      )}
    </div>
  );
};

export default BlogList;
